import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';
import { ChevronLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getReviewGeneration,
  retryReviewGenerationArtifact,
  upsertReviewGenerationReview,
  type GenerationReviewTarget,
  type GenerationRunDetail,
  type GenerationSessionNode,
} from '@/api/reviewStudio';
import { StudioGate } from '@/components/ReviewStudio/StudioGate';
import { VideoInspector } from '@/components/ReviewStudio/VideoInspector';
import { PromptBlock } from '@/components/ReviewStudio/ConfigPanel';
import { JsonDrawer } from '@/components/ReviewStudio/JsonDrawer';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';

export const Route = createFileRoute('/_auth/reviews/generations/$runId')({
  component: GenerationInspectorPage,
});

type NodeId =
  | 'overview'
  | 'strategy'
  | 'cover'
  | 'plan'
  | `session-${number}`
  | `session-${number}-stills`
  | `session-${number}-video`
  | `session-${number}-prompt`;

function GenerationInspectorPage() {
  return (
    <StudioGate>
      <GenerationInspectorBody />
    </StudioGate>
  );
}

function GenerationInspectorBody() {
  const { runId } = Route.useParams();
  const queryClient = useQueryClient();
  const [node, setNode] = useState<NodeId>('overview');
  const [coverPrompt, setCoverPrompt] = useState('');

  const query = useQuery({
    queryKey: ['review-generation', runId],
    queryFn: () => getReviewGeneration(runId),
    refetchInterval: (q) =>
      q.state.data?.generation_status === 'generating' ? 4000 : false,
  });

  const run = query.data;

  const retry = useMutation({
    mutationFn: (body: Parameters<typeof retryReviewGenerationArtifact>[1]) =>
      retryReviewGenerationArtifact(runId, body),
    onSuccess: (result) => {
      toast.success(result.message || 'Queued');
      queryClient.invalidateQueries({ queryKey: ['review-generation', runId] });
    },
    onError: () => toast.error('Retry failed'),
  });

  const score = useMutation({
    mutationFn: (body: {
      target: GenerationReviewTarget;
      target_id?: string;
      taste: number;
      quality: number;
      hallucination: number;
      notes: string;
    }) => upsertReviewGenerationReview(runId, body),
    onSuccess: () => {
      toast.success('Saved');
      queryClient.invalidateQueries({ queryKey: ['review-generation', runId] });
    },
    onError: () => toast.error('Could not save score'),
  });

  if (query.isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-40 animate-pulse rounded-full bg-neutral-100" />
        <div className="h-64 animate-pulse rounded-3xl bg-neutral-100" />
      </div>
    );
  }

  if (query.isError || !run) {
    return (
      <div className="rounded-3xl bg-white px-6 py-16 text-center ring-1 ring-black/5">
        <p className="text-sm font-medium text-neutral-900">Generation not found</p>
        <Link to="/reviews" search={{ tab: 'generations' }} className="mt-3 inline-block text-sm text-neutral-500 underline">
          Back
        </Link>
      </div>
    );
  }

  const nav: { id: NodeId; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'strategy', label: 'Strategy + prompts' },
    { id: 'plan', label: 'Plan dump' },
    { id: 'cover', label: 'Cover' },
    ...run.sessions.flatMap((session, i) => [
      { id: `session-${i}` as NodeId, label: session.title || `Session ${i + 1}` },
      { id: `session-${i}-prompt` as NodeId, label: `  · tutor prompt` },
      { id: `session-${i}-stills` as NodeId, label: `  · stills (${session.stills.length})` },
      {
        id: `session-${i}-video` as NodeId,
        label: `  · video (${session.video_stories.length})`,
      },
    ]),
  ];

  return (
    <div className="space-y-5">
      <Link
        to="/reviews"
        search={{ tab: 'generations' }}
        className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-900"
      >
        <ChevronLeft className="h-4 w-4" />
        Generations
      </Link>

      <div className="rounded-3xl bg-white px-5 py-4 ring-1 ring-black/5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-neutral-900">
              {run.name || 'Untitled generation'}
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-neutral-500">{run.user_prompt}</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <Chip>{run.generation_status || 'unknown'}</Chip>
              {run.pack_id ? <Chip>{run.pack_id}</Chip> : null}
              {run.video_policy ? <Chip>{run.video_policy}</Chip> : null}
              {run.deep_learn ? <Chip>deep</Chip> : <Chip>quick</Chip>}
              {run.hydrated_from_group ? <Chip>legacy group</Chip> : null}
            </div>
          </div>
          {run.cover_image_url ? (
            <img
              src={run.cover_image_url}
              alt=""
              className="h-20 w-32 rounded-2xl object-cover ring-1 ring-black/5"
            />
          ) : null}
        </div>
      </div>

      <div className="flex flex-col gap-5 lg:flex-row">
        <aside className="w-full shrink-0 space-y-1 lg:w-56">
          {nav.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setNode(item.id)}
              className={`block w-full rounded-full px-3 py-1.5 text-left text-[12px] ${
                node === item.id
                  ? 'bg-[#AAE73D] text-neutral-900'
                  : 'text-neutral-600 hover:bg-neutral-100'
              }`}
            >
              {item.label}
            </button>
          ))}
        </aside>

        <main className="min-w-0 flex-1 space-y-4">
          {node === 'overview' && <OverviewPanel run={run} />}
          {node === 'strategy' && <StrategyPanel strategy={run.strategy} />}
          {node === 'plan' && (
            <div className="rounded-3xl bg-white p-4 ring-1 ring-black/5">
              <JsonDrawer data={run.plan} label="Full plan JSON" />
            </div>
          )}
          {node === 'cover' && (
            <CoverPanel
              run={run}
              prompt={coverPrompt || run.cover_image_prompt}
              onPromptChange={setCoverPrompt}
              pending={retry.isPending}
              onRetry={() =>
                retry.mutate({
                  kind: 'cover',
                  prompt: coverPrompt || run.cover_image_prompt,
                })
              }
            />
          )}
          {node.startsWith('session-') && (
            <SessionNodePanel
              run={run}
              node={node}
              onRetryStill={(stillId, prompt) =>
                retry.mutate({
                  kind: 'still',
                  knowledge_item_id: stillId,
                  prompt,
                })
              }
              retryPending={retry.isPending}
              onScore={(body) => score.mutate(body)}
              scorePending={score.isPending}
              onRefresh={() =>
                queryClient.invalidateQueries({ queryKey: ['review-generation', runId] })
              }
            />
          )}
        </main>

        <aside className="w-full shrink-0 lg:w-72">
          <ScorePanel
            run={run}
            saving={score.isPending}
            onSave={(body) => score.mutate(body)}
          />
        </aside>
      </div>
    </div>
  );
}

function Chip({ children }: { children: ReactNode }) {
  return (
    <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] text-neutral-600">
      {children}
    </span>
  );
}

function OverviewPanel({ run }: { run: GenerationRunDetail }) {
  return (
    <div className="space-y-4 rounded-3xl bg-white p-4 ring-1 ring-black/5">
      <PromptBlock title="User prompt" text={run.user_prompt} empty="No user prompt" />
      <div className="grid grid-cols-2 gap-2 text-[11px]">
        <Meta label="Mode" value={run.mode} />
        <Meta label="Kind" value={run.kind} />
        <Meta label="Pack" value={run.pack_id} />
        <Meta label="Video policy" value={run.video_policy} />
        <Meta label="Group" value={run.group_id} />
        <Meta label="Sessions" value={String(run.sessions.length)} />
      </div>
      <div className="space-y-1">
        {run.stages.map((stage) => (
          <div
            key={stage.name}
            className="flex items-center justify-between rounded-2xl bg-neutral-50 px-3 py-2 text-[11px]"
          >
            <span className="font-mono text-neutral-700">{stage.name}</span>
            <span className="text-neutral-400">{stage.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <div className="rounded-2xl bg-neutral-50 px-3 py-2">
      <p className="text-[10px] uppercase tracking-wide text-neutral-400">{label}</p>
      <p className="mt-0.5 break-all font-mono text-neutral-800">{value}</p>
    </div>
  );
}

function StrategyPanel({ strategy }: { strategy: Record<string, unknown> }) {
  const models =
    strategy.models && typeof strategy.models === 'object'
      ? (strategy.models as Record<string, unknown>)
      : {};
  return (
    <div className="space-y-3 rounded-3xl bg-white p-4 ring-1 ring-black/5">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400">
        Strategy (edit these in Python, not here)
      </p>
      <div className="flex flex-wrap gap-1.5">
        {Object.entries(models).map(([key, value]) => (
          <Chip key={key}>
            {key}: {String(value)}
          </Chip>
        ))}
      </div>
      <PromptBlock
        title="Planning system prompt"
        text={String(strategy.planning_system_prompt || '')}
        empty="Not snapshotted"
      />
      <PromptBlock
        title="Pack planning addendum"
        text={String(strategy.planning_addendum || '')}
        empty="No pack addendum"
      />
      <PromptBlock
        title="Video need prompt"
        text={String(strategy.video_need_prompt || '')}
        empty="No video-need prompt"
      />
      <PromptBlock
        title="Video generate prompt"
        text={String(strategy.video_generate_prompt || '')}
        empty="No video generate prompt"
      />
      <PromptBlock
        title="Video render system prompt"
        text={String(strategy.video_render_system_prompt || '')}
        empty="No render system prompt"
      />
      <JsonDrawer data={strategy} label="Full strategy JSON" />
    </div>
  );
}

function CoverPanel({
  run,
  prompt,
  onPromptChange,
  pending,
  onRetry,
}: {
  run: GenerationRunDetail;
  prompt: string;
  onPromptChange: (value: string) => void;
  pending: boolean;
  onRetry: () => void;
}) {
  return (
    <div className="space-y-3 rounded-3xl bg-white p-4 ring-1 ring-black/5">
      {run.cover_image_url ? (
        <img
          src={run.cover_image_url}
          alt=""
          className="aspect-video w-full rounded-2xl object-cover"
        />
      ) : (
        <div className="flex aspect-video items-center justify-center rounded-2xl bg-neutral-50 text-sm text-neutral-400">
          Cover not ready
        </div>
      )}
      <Textarea
        value={prompt}
        onChange={(event) => onPromptChange(event.target.value)}
        className="min-h-28 rounded-2xl text-[12px]"
        placeholder="Cover image prompt"
      />
      <Button
        type="button"
        disabled={pending}
        onClick={onRetry}
        className="h-9 rounded-full bg-[#AAE73D] px-4 text-neutral-900 hover:bg-[#9CD830]"
      >
        Regen cover
      </Button>
    </div>
  );
}

function SessionNodePanel({
  run,
  node,
  onRetryStill,
  retryPending,
  onScore,
  scorePending,
  onRefresh,
}: {
  run: GenerationRunDetail;
  node: string;
  onRetryStill: (id: string, prompt: string) => void;
  retryPending: boolean;
  onScore: (body: {
    target: GenerationReviewTarget;
    target_id?: string;
    taste: number;
    quality: number;
    hallucination: number;
    notes: string;
  }) => void;
  scorePending: boolean;
  onRefresh: () => void;
}) {
  const match = /^session-(\d+)(?:-(prompt|stills|video))?$/.exec(node);
  if (!match) return null;
  const index = Number(match[1]);
  const part = match[2] || 'summary';
  const session = run.sessions[index];
  if (!session) return null;

  if (part === 'prompt') {
    return (
      <div className="space-y-3 rounded-3xl bg-white p-4 ring-1 ring-black/5">
        <h2 className="text-sm font-semibold text-neutral-900">{session.title} · tutor prompt</h2>
        <PromptBlock
          title="Session prompt (from plan / mind_config)"
          text={session.session_prompt}
          empty="No session prompt stored"
        />
        <JsonDrawer data={session.plan_session} label="Plan session object" />
        <JsonDrawer data={session.visual_budget} label="Visual budget" />
        <JsonDrawer data={session.video_story_plan} label="Video story plan" />
        <JsonDrawer data={session.mind_config} label="Mind config" />
      </div>
    );
  }

  if (part === 'stills') {
    return (
      <StillsPanel
        session={session}
        onRetry={onRetryStill}
        pending={retryPending}
        onScore={onScore}
        scorePending={scorePending}
      />
    );
  }

  if (part === 'video') {
    return (
      <div className="space-y-3">
        {session.video_stories.length === 0 ? (
          <div className="rounded-3xl bg-white px-6 py-12 text-center ring-1 ring-black/5">
            <p className="text-sm text-neutral-500">No video story on this session yet</p>
            {Object.keys(session.video_story_plan).length > 0 ? (
              <div className="mt-4 text-left">
                <JsonDrawer data={session.video_story_plan} label="Planned storyboard" />
              </div>
            ) : null}
          </div>
        ) : (
          <VideoInspector
            runId={run.id.startsWith('group:') ? undefined : run.id}
            stories={session.video_stories}
            onRetryStored={onRefresh}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-3xl bg-white p-4 ring-1 ring-black/5">
      <h2 className="text-sm font-semibold text-neutral-900">{session.title}</h2>
      <p className="text-sm text-neutral-500">{session.description}</p>
      <div className="flex flex-wrap gap-1.5">
        <Chip>{session.duration_minutes} min</Chip>
        <Chip>{session.stills.length} stills</Chip>
        <Chip>{session.video_stories.length} videos</Chip>
        {session.session_template_id ? (
          <Chip>{`${session.session_template_id.slice(0, 8)}…`}</Chip>
        ) : null}
      </div>
      <PromptBlock
        title="Intro / plan prompt preview"
        text={String(session.plan_session.prompt || session.session_prompt || '')}
        empty="No prompt yet"
      />
    </div>
  );
}

function StillsPanel({
  session,
  onRetry,
  pending,
  onScore,
  scorePending,
}: {
  session: GenerationSessionNode;
  onRetry: (id: string, prompt: string) => void;
  pending: boolean;
  onScore: (body: {
    target: GenerationReviewTarget;
    target_id?: string;
    taste: number;
    quality: number;
    hallucination: number;
    notes: string;
  }) => void;
  scorePending: boolean;
}) {
  const [prompts, setPrompts] = useState<Record<string, string>>({});

  if (!session.stills.length) {
    return (
      <div className="rounded-3xl bg-white px-6 py-12 text-center ring-1 ring-black/5">
        <p className="text-sm text-neutral-500">No stills yet</p>
        {session.image_prompts.length > 0 ? (
          <div className="mt-4 space-y-2 text-left">
            {session.image_prompts.map((prompt, i) => (
              <PromptBlock key={i} title={`Planned image ${i + 1}`} text={prompt} empty="" />
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {session.stills.map((still) => {
        const edited = prompts[still.id] ?? still.styled_prompt;
        return (
          <div key={still.id} className="rounded-3xl bg-white p-4 ring-1 ring-black/5">
            {still.url ? (
              <img src={still.url} alt="" className="aspect-video w-full rounded-2xl object-cover" />
            ) : (
              <div className="flex aspect-video items-center justify-center rounded-2xl bg-neutral-50 text-xs text-neutral-400">
                {still.status || 'missing'}
              </div>
            )}
            <div className="mt-3 space-y-2">
              <PromptBlock
                title="Original prompt"
                text={still.original_prompt}
                empty="No original prompt"
              />
              <Textarea
                value={edited}
                onChange={(event) =>
                  setPrompts((prev) => ({ ...prev, [still.id]: event.target.value }))
                }
                className="min-h-24 rounded-2xl text-[11px]"
              />
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  disabled={pending}
                  onClick={() => onRetry(still.id, edited)}
                  className="h-8 rounded-full bg-[#AAE73D] px-3 text-xs text-neutral-900 hover:bg-[#9CD830]"
                >
                  Regen still
                </Button>
                <Button
                  type="button"
                  disabled={scorePending}
                  variant="secondary"
                  onClick={() =>
                    onScore({
                      target: 'image',
                      target_id: still.id,
                      taste: 3,
                      quality: 3,
                      hallucination: 3,
                      notes: '',
                    })
                  }
                  className="h-8 rounded-full px-3 text-xs"
                >
                  Score 3/3/3
                </Button>
              </div>
              <JsonDrawer data={still.generation_debug} label="Still generation_debug" />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ScorePanel({
  run,
  saving,
  onSave,
}: {
  run: GenerationRunDetail;
  saving: boolean;
  onSave: (body: {
    target: GenerationReviewTarget;
    target_id?: string;
    taste: number;
    quality: number;
    hallucination: number;
    notes: string;
  }) => void;
}) {
  const existing = useMemo(
    () => run.reviews.find((r) => r.target === 'path' && (!r.target_id || r.target_id === run.id)),
    [run],
  );
  const [taste, setTaste] = useState(existing?.taste ?? 3);
  const [quality, setQuality] = useState(existing?.quality ?? 3);
  const [hallucination, setHallucination] = useState(existing?.hallucination ?? 3);
  const [notes, setNotes] = useState(existing?.notes ?? '');

  return (
    <div className="sticky top-20 space-y-3 rounded-3xl bg-white p-4 ring-1 ring-black/5">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400">
        Path score
      </p>
      <ScoreRow label="Taste" value={taste} onChange={setTaste} />
      <ScoreRow label="Quality" value={quality} onChange={setQuality} />
      <ScoreRow label="Grounding" value={hallucination} onChange={setHallucination} />
      <Textarea
        value={notes}
        onChange={(event) => setNotes(event.target.value)}
        placeholder="Notes"
        className="min-h-20 rounded-2xl text-[12px]"
      />
      <Button
        type="button"
        disabled={saving || run.id.startsWith('group:')}
        onClick={() =>
          onSave({
            target: 'path',
            target_id: run.id,
            taste,
            quality,
            hallucination,
            notes,
          })
        }
        className="h-9 w-full rounded-full bg-[#AAE73D] text-neutral-900 hover:bg-[#9CD830]"
      >
        {saving ? 'Saving…' : 'Save score'}
      </Button>
      {run.reviews.length > 0 ? (
        <div className="space-y-2 pt-2">
          {run.reviews.map((review) => (
            <div key={review.id} className="rounded-2xl bg-neutral-50 px-3 py-2 text-[11px]">
              <p className="uppercase tracking-wide text-neutral-400">
                {review.target} · T{review.taste} Q{review.quality} G{review.hallucination}
              </p>
              {review.notes ? (
                <p className="mt-1 text-neutral-700">{review.notes}</p>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function ScoreRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-2 text-[12px] text-neutral-600">
      <span>{label}</span>
      <Input
        type="number"
        min={1}
        max={5}
        value={value}
        onChange={(event) => onChange(Number(event.target.value) || 1)}
        className="h-8 w-16 rounded-full text-center"
      />
    </label>
  );
}
